import {
  type OutletResource,
  OutletsController_list_v1,
  type OutletTradingSummaryReportResource,
  ProfitAndLossController_tradingSummary_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden, isNotFound } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { EmptyState, LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import {
  currentBusinessDate,
  defaultPeriod,
  periodProblem,
  type ReportingPeriod,
} from './reporting-period';

/**
 * What one outlet took, what it handed back, what it spent, and what is left:
 * `GET /v1/reports/outlets/{outletId}/trading-summary`.
 *
 * **The report a manager gets where an owner would open the profit and loss**,
 * and the reason there are two screens rather than one with a role flag is the
 * reason there are two resources rather than one with a field filtered out.
 *
 * ## The type split is the security control, and this screen is the easy half
 *
 * `OutletTradingSummaryResource` and `OutletProfitAndLossResource` carry the
 * same nine fields; the second adds `costOfGoods`. That is the whole difference,
 * and it is why the route this screen reads is `@RequireOutletScope()` while the
 * profit and loss beside it is `@RequireRole('OWNER')` — the split is *exactly*
 * cost and margin, and `export.controller.ts` says so in those words.
 *
 * The consequence here is worth stating plainly, because it is the opposite of
 * what a careful reader expects: **there is no field on this resource that needs
 * a role check, so nothing on this screen is conditionally rendered.** Everything
 * the API hands over is safe to show to anybody who could reach the route. That
 * property is bought by the type — including cost here would not compile — and
 * a screen that started filtering fields would be re-implementing a guarantee it
 * already has, badly.
 *
 * What this screen must not do is *derive* what the type withheld. It computes
 * nothing. In particular it does not re-derive `netPaise`, which is covered
 * below.
 *
 * ## Revenue is split by kind, and stays split
 *
 * `goodsRevenuePaise` and `admissionRevenuePaise` are separate figures because
 * only goods have a cost of goods sold — a ticket is admission and not a good
 * (FR-709 as replaced, R-704). Summing them into one "revenue" line would erase
 * the distinction the owner's profit and loss is built on, so the statement
 * shows them as two rows and shows no total of the two.
 *
 * `assertedAdmissionRevenuePaise` is a third and separate thing again: admission
 * a person put into the books by hand after a till could not send its queued
 * record (FR-822, FR-823a). The document is blunt that it is **not** inside
 * `admissionRevenuePaise` and must never be added to it — one figure is what
 * devices measured and the other is what somebody asserted. It *is* a term in
 * the net, because the park really took the money. Its count is published
 * beside it and is rendered, because the amount alone cannot tell one incident
 * of ₹5,000 from fifty of ₹100.
 *
 * ## Refunds are a disclosure, not a subtraction
 *
 * `goodsRevenuePaise` arrives **already net of** the refunds counted in the same
 * period, so `refundsPaise` is published for what it says about the outlet
 * rather than as a term in anything: an outlet that took ₹5,120 and handed
 * ₹1,000 back is not the same outlet as one that quietly took ₹4,120, and the
 * two net alike. It is therefore rendered apart from the running figures, under
 * the net rather than above it, and the note beside it says it has already been
 * taken off. A reader who subtracted it a second time would be wrong by exactly
 * that much.
 *
 * ## `netPaise` is the server's arithmetic and is never recomputed
 *
 * It is derived on every read and stored nowhere (R-701), and
 * `docs/ui-conventions.md` is explicit that the client does not compute totals.
 * So this screen renders what arrived. If the terms on screen ever fail to add
 * up to the net beside them, the honest thing is that the discrepancy is visible
 * — that is a defect to report to the API, and a screen that quietly agreed with
 * its own sum instead would be the one place it could never be found.
 *
 * It is also **not** a profit and not a margin, and the screen says so: nothing
 * in this report has subtracted what the goods cost. "Net" reads as profit to
 * somebody in a hurry, and this is a report a manager runs in a hurry.
 *
 * ## The period, and the outlet
 *
 * `from` and `to` are required, so the screen opens on a period rather than on
 * nothing — `reporting-period.ts` holds the default and the three ways a range
 * is refused, and the note at its head is where the clock question is answered.
 * The `to` picker is bounded at the park's current trading day, because a period
 * ending after it is refused `REPORTING_PERIOD_IN_FUTURE`; the notice under the
 * pickers stays as the second line of defence, since the day rolls at midnight
 * in Kolkata and a screen left open overnight would bound itself at yesterday.
 *
 * The outlet is a **path** parameter and there is no "every outlet" — this route
 * reports on one. So the screen waits for a choice rather than picking one, the
 * same shape `DayClose` settled on for the same reason. Retired outlets are
 * offered, because an outlet retired last month still traded the month before
 * and a period that could not name it would read as a fault.
 *
 * ## What is deliberately not here
 *
 * **No CSV export.** The cash book and the profit and loss each have one and
 * this route has none, which is the one place the three Reporting reads differ
 * in shape. Nothing here works around that.
 */

/** Every state the statement can be in. One value, so two cannot coexist. */
type SummaryState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly report: OutletTradingSummaryReportResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Nothing chosen. Not a value the route would accept, which is the point. */
const NO_OUTLET = '';

export function TradingSummary() {
  const client = useApiClient();

  const [outletId, setOutletId] = useState<string>(NO_OUTLET);

  // The period the statement is read over, and the period the pickers show.
  // They are the same until somebody starts changing it: a half-cleared range is
  // not a request, so the last complete one stays on screen while a new one is
  // chosen.
  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));

  const [state, setState] = useState<SummaryState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  const problem = periodProblem(period, systemClock);

  // The park's own day, in Asia/Kolkata — not the device's. Read once per render
  // rather than held in state: a screen left open across midnight should bound
  // itself at the new day, not at the day it opened on.
  const today = currentBusinessDate(systemClock);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones included —
    // this is a report over a past period, and an outlet retired since it still
    // traded during it.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The picker is the only casualty. Not `outletsAnswered`: a failed read
        // has answered nothing about which outlets exist.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // No outlet is not a request — the route reports on one and there is no
    // "every outlet". A half-filled or refused period is not one either: the
    // notice under the pickers is already saying so, and sending it would earn a
    // 422 for something the screen can see.
    if (outletId === NO_OUTLET || period.from === '' || period.to === '' || problem !== null) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(ProfitAndLossController_tradingSummary_v1, {
        path: { outletId },
        query: { from: period.from, to: period.to },
        signal: controller.signal,
      })
      .then((report) => {
        if (live) setState({ kind: 'loaded', report });
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, period.from, period.to, problem, reloads]);

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  const chosenOutlet = outlets.find((outlet) => outlet.id === outletId);

  return (
    <Screen
      title="Trading summary"
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="trading-summary-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                // Base UI types the value as nullable because a select may be
                // cleared. `''` is what this screen calls "nothing chosen", and
                // it is never sent.
                setOutletId(next ?? NO_OUTLET);
              }}
              disabled={outlets.length === 0}
            >
              <SelectTrigger
                id="trading-summary-outlet"
                aria-label="Outlet"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue placeholder="Choose an outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {outletsAnswered && outlets.length === 0 && (
              <p className="text-sm text-muted-foreground">
                This account is not posted to any outlet.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="trading-summary-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="trading-summary-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, from: next }));
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="trading-summary-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="trading-summary-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              // The route refuses a period ending after the park's current
              // trading day, so a day past it is not offered. The notice below
              // still stands — see `maxDay` on `DayPicker` for why both.
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, to: next }));
              }}
            />
          </div>

          {/* Under the pickers rather than above the statement: it is about the
              two controls immediately above it, and it is not a failed read. */}
          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <ScreenFact label="Period">
            {period.from === '' || period.to === ''
              ? 'Choose both days.'
              : `${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}`}
          </ScreenFact>

          {chosenOutlet !== undefined && (
            <ScreenFact label="Outlet">
              <span className="font-mono numeric">{chosenOutlet.code}</span> — {chosenOutlet.name}
              {isRetired(chosenOutlet) && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an outlet is an
                      ordinary end to an ordinary life, and it still traded. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
            </ScreenFact>
          )}
        </>
      }
    >
      {state.kind === 'idle' && (
        <Panel title="Choose an outlet to report on">
          <p className="text-sm text-muted-foreground">Choose the outlet, and both days.</p>
        </Panel>
      )}

      {state.kind === 'loading' && <StatementSkeleton />}

      {state.kind === 'failed' && (
        <Notice>
          {isForbidden(state.error) ? (
            <EmptyState
              title="This account cannot read this trading summary"
              description="If you are an owner or manager, sign out and back in."
            />
          ) : isNotFound(state.error) ? (
            // The route answers 404 for an outlet that does not exist and for one
            // outside the caller's reach, identically and on purpose. Said as the
            // one sentence that covers both, rather than as
            // `RESOURCE_NOT_FOUND`'s "go back and refresh the list" — there is no
            // list here, and guessing which of the two it was is precisely the
            // disclosure the API declined to make.
            <EmptyState
              title="That outlet is not one this account can report on"
              description="Choose another outlet."
            />
          ) : (
            <LoadFailed
              error={state.error}
              onRetry={() => {
                setReloads((count) => count + 1);
              }}
              title="This trading summary could not be read"
            />
          )}
        </Notice>
      )}

      {state.kind === 'loaded' && <Statement report={state.report} />}
    </Screen>
  );
}

/**
 * The statement: five figures that run to a net, and the refund disclosure that
 * deliberately does not.
 *
 * Named by the outlet, so the region a reader lands on says which outlet's
 * figures these are without their having to look back at the rail.
 */
function Statement({ report }: { report: OutletTradingSummaryReportResource }) {
  const { outlet, period } = report;

  return (
    <Panel
      title={outlet.outletName}
      description={`${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}, both days included.`}
    >
      {/* `hadTrading` is an answer and not a refusal, and the figures stay
          beside it: an outlet that was shut but paid for a delivery still has an
          expense to show, and hiding it behind "nothing happened" would
          contradict the expense log. */}
      {!period.hadTrading && (
        <p role="status" className="mb-5 text-sm text-muted-foreground">
          Nothing traded at this outlet between these two days.
        </p>
      )}

      <dl>
        {/* Two revenue lines and no sum of them: only goods carry a cost of
            goods sold, and that distinction is what the owner's profit and loss
            is built on. */}
        <Figure term="Goods sold">{outlet.goodsRevenuePaise}</Figure>
        <Figure term="Admission">{outlet.admissionRevenuePaise}</Figure>
        <Figure term="Admission entered by hand">{outlet.assertedAdmissionRevenuePaise}</Figure>
        <Figure term="Expenses">{outlet.expensesPaise}</Figure>
        <Figure term="Net for the period" strong>
          {outlet.netPaise}
        </Figure>
      </dl>

      {/* Two lines kept by #348's rule: the count, because the hand-entered
          amount alone cannot tell one incident of ₹5,000 from fifty of ₹100;
          and the warning, because "net" reads as profit to somebody in a hurry. */}
      <div className="mt-4 space-y-2 text-sm text-muted-foreground">
        {outlet.assertedAdmissionCount > 0 && (
          <p>{`${String(outlet.assertedAdmissionCount)} ${outlet.assertedAdmissionCount === 1 ? 'admission' : 'admissions'} entered by hand.`}</p>
        )}
        <p>Not a profit figure: what the goods cost is not in this report.</p>
      </div>

      {/* Below the net and outside the running figures, because it is a
          disclosure rather than a term: `goodsRevenuePaise` has already had it
          removed. */}
      <div className="mt-6 border-t border-border pt-4">
        <dl>
          <Figure term="Handed back">{outlet.refundsPaise}</Figure>
        </dl>
        <p className="mt-2 text-sm text-muted-foreground">
          This is already out of goods sold above. Do not take it off again.
        </p>
      </div>
    </Panel>
  );
}

/**
 * One line of the statement: a term and its amount.
 *
 * Amounts right-align and carry `numeric`, per `docs/ui-conventions.md` — Geist's
 * default figures are proportional, and reading down a column of money is
 * exactly what this screen is for.
 *
 * A definition list and not a table: these are five labelled values rather than
 * rows of records, and `<dt>`/`<dd>` is what pairs a label with its value for
 * somebody reading with a screen reader.
 */
function Figure({
  term,
  children,
  strong = false,
}: {
  term: string;
  children: number;
  strong?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-2 ${strong ? 'mt-1 border-t border-border pt-3' : ''}`}
    >
      <dt className={strong ? 'text-foreground' : 'text-muted-foreground'}>{term}</dt>
      <dd
        className={`text-right font-mono numeric ${strong ? 'font-medium text-foreground' : 'text-foreground'}`}
      >
        {formatPaise(children)}
      </dd>
    </div>
  );
}

/**
 * A card carrying one of the states where there is no statement to show.
 *
 * No heading, because `EmptyState` and `LoadFailed` each supply their own and a
 * panel title above one of those says the same thing twice. The centring and the
 * measure are `TableNotice`'s, which is where every list screen in the app puts
 * these two — they are written to be read as a block in the middle of an empty
 * field, and `LoadFailed` centres its own title on that assumption.
 */
function Notice({ children }: { children: ReactNode }) {
  return (
    // **No box.** The panel is the surface, so the old card was a card inside a
    // card. What it was doing was centring a measure in an otherwise empty
    // field, which is `TableNotice`'s job on every list screen; `flex-1` does it
    // here, so a screen with nothing chosen reads like a list with no rows.
    <div className="flex min-h-0 flex-1 items-center justify-center py-14">
      <div className="mx-auto max-w-md space-y-3 text-center">{children}</div>
    </div>
  );
}

/**
 * A band of the page.
 *
 * The same composition `DayClose` uses, and this is now the second screen that
 * wants it — which is the moment `docs/ui-conventions.md` says to promote it,
 * to `back-office/Screen.tsx` beside `ScreenFact` and `EmptyState`. That move
 * touches another epic's screen, so it is named in the PR body rather than taken
 * here while three Reporting tickets are in flight against the same files.
 */
function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  const headingId = useId();

  return (
    // **A ruled region, not a card** — a heading and a hairline. The gutter goes
    // with the border, so the amounts right-align to the field's own edge, the
    // same edge the head and the toolbar use.
    <section aria-labelledby={headingId} className="pt-2">
      <header className="border-b border-border pb-3">
        <h2 id={headingId} className="font-medium text-foreground">
          {title}
        </h2>
        {description !== undefined && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </header>
      <div className="pt-5">{children}</div>
    </section>
  );
}

/**
 * The statement's shape while it is in flight.
 *
 * A skeleton and not a spinner, at the height the loaded panel settles at, so
 * the page does not jump when the figures land. `aria-hidden` because there is
 * nothing here to read.
 */
function StatementSkeleton() {
  return <div aria-hidden className="h-[26rem] animate-pulse rounded-2xl bg-muted" />;
}
